import { useEffect, useState } from 'react';
import { useNextStep } from 'hooks/useStep';
import { useGetQuestions } from 'api/hooks/useSurveyQuestions';
import { LeftOffDialog } from './LeftOffDialog';
import { Carousel } from '../../components/Carousel';
import { useUser } from '../../context/userContext';
import { checkQuestionsAnswered, allQuestionsAnswered, isAnswerEmpty, hasAnsweredQuestionsInSection } from '../../utils';
import Loading from '../../components/Loading';
import { getLeftOff } from '../../persistence/localProgress';
import { AnswersItem, Maybe, Question } from '../../__generated__/graphql';

const whereLeftOff = (allAnswers: boolean | undefined, questions: Maybe<Question>, answers: Maybe<AnswersItem>, companySlug: string) => {
  if (!isAnswerEmpty(answers)) {
    if (allAnswers) {
      return `/${companySlug}/results-intro`;
    }

    // Check completion status for all sections
    const valuesComplete = checkQuestionsAnswered(questions?.values, answers);
    const valuesStarted = hasAnsweredQuestionsInSection(questions?.values, answers);

    const interestsComplete = checkQuestionsAnswered(questions?.interests, answers);
    const interestsStarted = hasAnsweredQuestionsInSection(questions?.interests, answers);

    const personalityComplete = checkQuestionsAnswered(questions?.personality, answers);
    const personalityStarted = hasAnsweredQuestionsInSection(questions?.personality, answers);

    // PRIORITY 1: Any started but incomplete section (prevents section skipping)
    if (personalityStarted && !personalityComplete) {
      return `/${companySlug}/questions/personality`;
    }

    if (interestsStarted && !interestsComplete) {
      return `/${companySlug}/questions/interests`;
    }

    if (valuesStarted && !valuesComplete) {
      return `/${companySlug}/questions/values`;
    }

    // PRIORITY 2: Follow normal section order for unstarted sections
    if (!personalityComplete) {
      return `/${companySlug}/questions/personality`;
    }
    if (!interestsComplete) {
      return `/${companySlug}/questions/interests`;
    }
    if (!valuesComplete) {
      return `/${companySlug}/questions/values`;
    }
  }
};

const AppInstructions = () => {
  const { nextStep } = useNextStep();
  const carousel: { image: string; caption?: string }[] = [
    {
      image: '/companyLogo.png',
      caption:
        // eslint-disable-next-line max-len
        'Welcome to TeamFit, a simple values and personality survey. It only takes 5 minutes and can be completed on your phone or a computer.',
    },
    {
      image: '../img/swipe.png',
      caption: 'Thumbs up or thumbs down to answer simple questions. Remember, there are no wrong or right answers!',
    },
    {
      image: '/high-five.png',
      caption:
        'The results will help us get to know you better. We will send you the results too, so you can understand and promote your strengths when applying for roles.',
    },
  ];

  const [leftOffUrl, setLeftOffUrl] = useState<string>();
  const { answers, userLoading, company, companyLoading, jobRoleId, clear } = useUser();
  const { questions, questionLoading } = useGetQuestions({ companyId: company?.id || '', jobRoleId });

  useEffect(() => {
    const allAnswers = allQuestionsAnswered(questions, answers);
    const defaultLeftOffUrl = whereLeftOff(allAnswers, questions, answers, company?.slug || '');

    // Only show left-off for users with an existing session (backend answers present)
    if (!isAnswerEmpty(answers)) {
      // Prefer the most recent local section pointer if available
      const local = getLeftOff(company?.id);
      const target = local?.section ? `/${company?.slug}/questions/${local.section}` : defaultLeftOffUrl;
      if (target) setLeftOffUrl(target);
    } else {
      setLeftOffUrl(undefined);
    }
  }, [answers, questions, company]);

  if (userLoading || companyLoading || questionLoading) return <Loading />;

  const handleGetStarted = () => {
    clear();
    nextStep();
  };

  return (
    <div className="flex flex-col items-center justify-around text-center text-white h-5/6 pt-6">
      <div className="ps-3">
        <div className="text-white text-xl text-center sm:text-display-sm font-semibold leading-xl sm:leading-display-sm mb-1">TeamFit {company?.name}</div>
      </div>
      <div className="z-10 mb-2">
        <Carousel items={carousel} />
      </div>
      {!leftOffUrl && (
        <button type="button" className="bg-yellow-500 text-black 	p-2	uppercase rounded-lg font-semibold text-md " onClick={handleGetStarted}>
          Get started
        </button>
      )}
      {leftOffUrl && <LeftOffDialog key={1} setLeftOffUrl={setLeftOffUrl} leftOffUrl={leftOffUrl} onStartAgain={handleGetStarted} />}
    </div>
  );
};

export { AppInstructions };
